import { ErrorHandler, inject, Injectable } from '@angular/core';
import { AppUpdateService } from '../services/app-update.service';
import { Router } from '@angular/router';

/**
 * Handles webpack ChunkLoadError that occurs in production when a user has
 * an old bundle cached and tries to lazy-load a route whose chunk filename
 * has changed after a new deployment.
 *
 * Symptom: "TypeError: Cannot read properties of undefined (reading 'call')"
 * at webpack_require (bootstrap:19:1)
 *
 * Fix: notify AppUpdateService so the UI can show a banner prompting the user
 * to reload at their convenience, preserving any unsaved work.
 */
@Injectable()
export class ChunkLoadErrorHandler extends ErrorHandler {
  private appUpdate = inject(AppUpdateService);
  private router = inject(Router);

  override handleError(error: unknown): void {
    if (!this.isChunkLoadError(error)) {
      super.handleError(error);
      return;
    }

    const userIsInAuthModule = this.router.url.startsWith('/auth');
    if (userIsInAuthModule) {
      // If the user is on the auth module, they will likely be redirected to the main shell after reloading.
      this.appUpdate.reloadToUpdate();
      return;
    }

    console.error(
      '[ChunkLoadError] Stale bundle detected. Prompting user to reload.',
      error
    );
    this.appUpdate.notifyChunkError();
  }

  private isChunkLoadError(error: unknown): boolean {
    if (!(error instanceof Error)) return false;

    return (
      error.name === 'ChunkLoadError' ||
      /loading chunk \S+ failed/i.test(error.message) ||
      /cannot read propert\S+ of undefined.*call/i.test(error.message) ||
      error.message.includes('webpack_require')
    );
  }
}
